import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setUserTokenAction } from "../redux/userSlice";
import {
  fetchHubEntitlement,
  fetchHubSession,
  hubBaseUrl,
  hubHosted,
  loadHubHeaderScript,
  hubAuth,
  rememberHubBomId,
  rememberHubProjectId,
} from "lib/hubSso";
import Spinner from "components/Spinner";

/**
 * Semi360 Hub SSO + entitlement gate.
 *
 * Hosted (qbuy360.dev.semi360.ai): drives the Hub session flow before the app
 * renders — not signed in bounces to the Hub sign-in; qbuy360 access "none"
 * shows an upgrade gate; otherwise it stores the product JWT and renders
 * children. It also periodically refreshes the short-lived JWT so long sessions
 * do not expire mid-use.
 *
 * Local/dev (localhost): renders children immediately and leaves the existing
 * local login flow completely intact.
 */
export default function HubGate({ children }) {
  const [state, setState] = useState(() => (hubHosted() ? { mode: "loading" } : { mode: "local" }));
  const dispatch = useDispatch();

  useEffect(() => {
    if (!hubHosted()) return undefined;

    let cancelled = false;
    loadHubHeaderScript();

    // Capture ?bom= / ?prj= before anything can navigate away from the entry URL.
    // The board id is kept, not just stored: the rewrite below needs to know
    // whether this visit is a hand-off.
    const sentBomId = rememberHubBomId();
    rememberHubProjectId();

    const signin = () => {
      window.location.href = `${hubBaseUrl()}/signin?next=${encodeURIComponent(window.location.href)}`;
    };

    // Bootstrap with bounded retries. Only a DEFINITIVE "not authenticated"
    // from the Hub bounces to sign-in: a transport error (Hub briefly
    // unreachable, network blip) must not eject a genuinely signed-in user.
    const run = async (attempt = 0) => {
      const session = await fetchHubSession();
      if (cancelled) return;

      if (session && session.authenticated === false) {
        signin();
        return;
      }
      if (!session) {
        if (attempt < 4) {
          window.setTimeout(() => run(attempt + 1), 800 * (attempt + 1));
          return;
        }
        // Exhausted retries with the Hub still unreachable -> sign-in.
        signin();
        return;
      }

      // Signed in -> get the product JWT the backend verifies.
      await hubAuth().refresh();
      if (cancelled) return;

      const entitlement = await fetchHubEntitlement();
      if (cancelled) return;

      if (!entitlement || entitlement.access === "none") {
        setState({ mode: "gated", session });
        return;
      }

      // The token in localStorage authenticates the API, but the app's own
      // notion of "signed in" is the redux user the local login would have
      // set. Without seeding it, every screen that reads it — the header,
      // the sidebar's role filter — renders the signed-out shell around a
      // signed-in session. Seed it from the Hub session the way the local
      // login seeds it from its response.
      let token = null;
      try {
        token = localStorage.getItem("token");
      } catch {
        /* private mode */
      }
      dispatch(
        setUserTokenAction({
          user: {
            name: session.user?.name || session.user?.email || "",
            email: session.user?.email || "",
            company_name: session.org?.name || "",
            // The Hub's org-management role; the backend mirrors the same
            // value onto tri_admin.company_role, so both ends agree.
            company_role: session.org?.role || "member",
          },
          token,
        }),
      );

      // qbuy360's "/" route is its own login page — the one screen a
      // Hub-authenticated visit must never land on. The switcher and the
      // ?bom= hand-off both arrive at "/", so rewrite it BEFORE the router
      // mounts; deep links keep their path.
      //
      // Where it rewrites TO depends on whether a board came with it. The
      // dashboard does not read the parked id — only /uploaded-bom claims it
      // — so sending a hand-off there stored the board in sessionStorage and
      // then showed an ordinary dashboard, which is indistinguishable from
      // the send having done nothing. Any sender that lands on "/" with a
      // ?bom= (a saved link, an older Hub build, an integration) hit that
      // dead end; the id decides the destination now, not the path alone.
      const path = window.location.pathname;
      if (path === "/" || path === "/register" || path === "/forgot-password") {
        const dest = sentBomId ? "/uploaded-bom" : "/dashboard";
        window.history.replaceState(null, "", dest + window.location.search);
      }

      setState({ mode: "ready", session, entitlement });
    };

    run();

    // Forward the embedded header's hub-feature intents to the Hub.
    const onFeature = (e) => {
      const detail = (e && e.detail) || {};
      const id = detail.feature || detail.id || "";
      window.location.href = `${hubBaseUrl()}/hub?feature=${encodeURIComponent(id)}`;
    };
    document.addEventListener("semi360:hub-feature", onFeature);

    // Keep the short-lived product JWT fresh (every ~4 min).
    const iv = window.setInterval(() => {
      hubAuth().refresh();
    }, 4 * 60 * 1000);

    return () => {
      cancelled = true;
      document.removeEventListener("semi360:hub-feature", onFeature);
      window.clearInterval(iv);
    };
  }, []);

  if (state.mode === "loading") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          font: "var(--page-head-sub, 400 12.5px/16px system-ui, sans-serif)",
          color: "var(--fg-3, #6b7280)",
        }}
      >
        <Spinner bare label="Signing in to Semi360…" size={48} />
      </div>
    );
  }

  if (state.mode === "gated") {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
        <div style={{ maxWidth: 420, textAlign: "center" }}>
          <h1
            style={{
              font: "var(--page-head-title, 600 20px/28px system-ui, sans-serif)",
              color: "var(--fg-1, #191334)",
              margin: 0,
            }}
          >
            QBuy360 is not on your plan
          </h1>
          <p style={{ color: "var(--fg-3, #6b7280)", marginTop: 8 }}>
            Raising POs across suppliers needs a plan that includes QBuy360.
          </p>
          <a
            href={`${hubBaseUrl()}/hub?feature=plans`}
            style={{
              display: "inline-block",
              marginTop: 16,
              padding: "8px 16px",
              borderRadius: "var(--radius-sm, 6px)",
              background: "var(--brand-violet, #5F47C9)",
              color: "#fff",
              textDecoration: "none",
            }}
          >
            See plans
          </a>
        </div>
      </div>
    );
  }

  // 'local' and 'ready' both render the app.
  return children;
}
